import { useEffect } from 'react'
import type { GetServerSidePropsContext } from 'next'
import { BogoBanner, BogoPromoModal } from '@mc/bogo'
import { getBucket } from '@mc/next-growthbook'
import { PAGE_SOURCES, PromoExplainerModal, DiscountModal } from '@mc/promos'
import { StaticDataProvider } from '@mc/static-data-react'
import { TRACK_PROPS, trackPage } from '@mc/track-event'
import { TrackingParamsProvider } from '@mc/tracking-context'
import GuestPassBanner from 'components/GuestPassBanner'
import { GoogleOneTap } from 'experiments/pen-3951_v2_google_email_capture/components/GoogleOneTap'
import { CMInnovativeSwingV2Experiment } from 'experiments/pen-5613_v2_cm_innovative_swing'
import { QuizEntryModalLoader } from 'experiments/pen-5646_v1_quiz_entry_sticky_footer/client'
import {
  getModalFeaturedClasses,
  QUIZ_ENTRY_IN_STICKY_FOOTER_EXP_FLAG,
} from 'experiments/pen-5646_v1_quiz_entry_sticky_footer/server'
import { trackEmailCaptureInPromoModalBackendEv } from 'experiments/pen-5891_v1_email_capture_in_promo_modal'
import { ssrDynamicHero } from 'libs/dynamic-hero'
import { trackProductViewedCourse } from 'libs/events'
import {
  getV2Categories,
  getCourseMeta,
  getCoursesRelatedToCourse,
  getArticlesRelatedToCourse,
  getFreeTrialByToken,
  getDefaultProduct,
  getMobileContentGems,
  getCourseLegacyTaxonomy,
} from 'libs/server/api'
import {
  getGrowthClassCategories,
  getGrowthClassesBySubcategoryIds,
  getGrowthClassSubcategories,
  getPrimaryCategoryAndSubcategoryForGrowthClass,
} from 'libs/server/api/growth'
import { getPageData } from 'libs/server/page-data'
import { pageErrorHandler } from 'libs/server/page-error-handler'
import { transformCourseRedirect } from 'libs/server/redirect'
import { useSprig } from 'libs/sprig'
import { redirect } from 'libs/utils'
import { getSSRQuizOnlyOnceCookie } from 'libs/utils/quizOnlyOnce'
import { getClassSpecialType } from './classSpecialType'
import Meta from './components/Meta'
import { trackViewedClass } from './trackViewedClass'
import type { CoursePageProps } from './types'
import { getArticleCategory } from './utils/articleCategories'

const Course = ({
  course,
  metadata,
  trial,
  primaryGrowthCatAndSubcat,
  annualPass,
  relatedArticles,
  relatedCourses,
  dynamicHero,
  savedDynamicHero,
  utmDynamic,
  ctaNextPage,
  classSpecialType,
  classSubcategories,
  otherClassesInSameSubcategories,
  classCategories,
  mobileContentGems,
  quizEntryModalFeaturedClasses,
}: CoursePageProps) => {
  useEffect(() => {
    if (annualPass) {
      trackProductViewedCourse({
        annualPass,
        course,
        coursePrimaryCategory: primaryGrowthCatAndSubcat.primaryCategory,
        courseCategoryIds: classCategories.map((cat) => cat.id),
        primarySubcategory: primaryGrowthCatAndSubcat?.primarySubcategory,
        courseSubcategoriesIds: classSubcategories.map((subcat) => subcat.id),
      })
    }
  }, [
    course,
    primaryGrowthCatAndSubcat,
    annualPass,
    classSubcategories,
    classCategories,
  ])

  useEffect(() => {
    trackPage('Course Marketing', {
      saved_completed_quiz: !!ctaNextPage,
      platform: 'next',
    })
  }, [course.slug, ctaNextPage])

  useSprig()

  return (
    <TrackingParamsProvider value={{ [TRACK_PROPS.PAGE_TYPE]: 'next-classes' }}>
      <StaticDataProvider
        value={{
          annualPass,
          course,
          trial,
          metadata,
          primaryGrowthCatAndSubcat,
          relatedArticles,
          relatedCourses,
          dynamicHero,
          savedDynamicHero,
          utmDynamic,
          ctaNextPage,
          classSpecialType,
          classSubcategories,
          otherClassesInSameSubcategories,
          classCategories,
          mobileContentGems,
          quizEntryModalFeaturedClasses,
        }}
        providerKey={course.slug}
      >
        <Meta />
        {/* Modals */}
        <BogoPromoModal />
        <PromoExplainerModal pageSource={PAGE_SOURCES.CM_PAGE} />
        <DiscountModal />
        <QuizEntryModalLoader />
        {/* Banners */}
        <BogoBanner />
        <GuestPassBanner />
        {/* Page Content */}
        <CourseContent />
        {/* Google One Tap */}
        <GoogleOneTap />
      </StaticDataProvider>
    </TrackingParamsProvider>
  )
}

const CourseContent = () => <CMInnovativeSwingV2Experiment />

Course.pageSettings = {
  showPromo: true,
  // analyticsLabel intentionally missing: trackPage is called above with extra attributes (quiz only once)
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const {
    query: { slug: slugQuery },
    req: {
      cookies: { referral_token: referralToken },
    },
    resolvedUrl,
  } = ctx
  if (typeof slugQuery !== 'string') {
    return pageErrorHandler(new Error('Invalid slug'))
  }

  let course
  try {
    course = await getCourseLegacyTaxonomy(ctx, { slug: slugQuery })
  } catch (err) {
    return pageErrorHandler(err)
  }

  const { slug } = course
  if (slug !== slugQuery) {
    return redirect(ctx, `/classes/${slug}`, { queryExclude: ['slug'] })
  }

  trackViewedClass(ctx.req, ctx.res, slug)

  const { dynamicHero, savedDynamicHero, utmDynamic } = await ssrDynamicHero(
    ctx,
    slug,
  )

  const primaryGrowthCatAndSubcat =
    await getPrimaryCategoryAndSubcategoryForGrowthClass(ctx, slug)

  const articleCategorySlug = getArticleCategory(
    primaryGrowthCatAndSubcat?.primaryCategory?.slug || '',
    true,
  )

  const ctaNextPage = await getSSRQuizOnlyOnceCookie(ctx)

  const quizEntryExperimentBucket = await getBucket(
    ctx,
    QUIZ_ENTRY_IN_STICKY_FOOTER_EXP_FLAG,
  )

  const shouldFetchQuizEntryClasses =
    quizEntryExperimentBucket?.startsWith('variant')

  const classSpecialType = getClassSpecialType(course)

  const classSubcategories = await getGrowthClassSubcategories(ctx, slug)

  await trackEmailCaptureInPromoModalBackendEv(ctx)

  const data = await getPageData({
    criticalPromises: {
      metadata: getCourseMeta(ctx, { slug }),
    },
    nonCriticalPromises: {
      annualPass: getDefaultProduct(ctx),
      relatedCourses: getCoursesRelatedToCourse(ctx, { slug }),
      relatedArticles: getArticlesRelatedToCourse(ctx, articleCategorySlug),
      trial: getFreeTrialByToken(ctx, { referralToken }),
      classCategories: getGrowthClassCategories(ctx, slug),
      otherClassesInSameSubcategories: getGrowthClassesBySubcategoryIds(
        ctx,
        classSubcategories.map((subcat) => subcat.id),
      ),
      categories: getV2Categories(ctx),
      mobileContentGems: getMobileContentGems(ctx, { id: course.id }),
      quizEntryModalFeaturedClasses: shouldFetchQuizEntryClasses
        ? getModalFeaturedClasses(ctx)
        : Promise.resolve([]),
    },
    propsMapper: (restProps) => ({
      course,
      dynamicHero: dynamicHero || {},
      utmDynamic: utmDynamic || {},
      savedDynamicHero,
      ctaNextPage,
      classSpecialType,
      classSubcategories,
      primaryGrowthCatAndSubcat,
      ...restProps,
    }),
  })

  return transformCourseRedirect(slug, data, resolvedUrl)
}

export default Course
